import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPhone } from 'react-icons/fi';
import { getApiUrl } from '../../utils/apiUrl';

const API_URL = getApiUrl();

// Default fallback videos (will be used if API returns empty)
const DEFAULT_VIDEOBOOTH_SHORTS = [
  { id: 'Zu7bq5EIGRM', title: 'Réalisation 1' },
  { id: 'hQppsiL6tQk', title: 'Réalisation 2' },
  { id: 'UCbA-AiQUJk', title: 'Réalisation 3' },
  { id: 'sq54E-WJmEQ', title: 'Réalisation 4' },
  { id: '6Lz40JU8dNw', title: 'Réalisation 5' },
];

const DEFAULT_LIVREDOOR_VIDEOS = [
  { id: '9IVOkEDnWr8', title: 'Livre d\'Or Vidéo - Exemple 1' },
  { id: '0Ob8z7NvJes', title: 'Livre d\'Or Vidéo - Exemple 2' },
  { id: '6bua6QI6rcc', title: 'Livre d\'Or Vidéo - Exemple 3' },
  { id: 'W4-O3vzi-1E', title: 'Livre d\'Or Vidéo - Exemple 4' },
];

// YouTube Shorts Embed Component (vertical format)
function YouTubeShort({ videoId, title }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative overflow-hidden rounded-2xl shadow-lg bg-black"
      style={{ aspectRatio: '9/16' }}
    >
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?loop=1&playlist=${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    </motion.div>
  );
}

// YouTube Video Embed Component (horizontal format)
function YouTubeVideo({ videoId, title }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative overflow-hidden rounded-2xl shadow-lg bg-black"
      style={{ aspectRatio: '16/9' }}
    >
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      />
    </motion.div>
  );
}

export default function RealisationsPage({ serviceType = 'videobooth-360' }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isVideoBooth = serviceType === 'videobooth-360';
  const isLivreDor = serviceType === 'livre-dor';

  // Load videos from API
  useEffect(() => {
    const loadRealizations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/realizations`);
        if (response.ok) {
          const data = await response.json();
          const allRealizations = data.realizations || [];
          
          // Filter by category
          const filtered = allRealizations
            .filter(r => r.category === serviceType)
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            .map(r => ({ id: r.videoId, title: r.title }));
          
          // Use defaults if empty
          if (filtered.length > 0) {
            setVideos(filtered);
          } else {
            setVideos(isVideoBooth ? DEFAULT_VIDEOBOOTH_SHORTS : DEFAULT_LIVREDOOR_VIDEOS);
          }
        } else {
          // Fallback to defaults
          setVideos(isVideoBooth ? DEFAULT_VIDEOBOOTH_SHORTS : DEFAULT_LIVREDOOR_VIDEOS);
        }
      } catch (error) {
        console.error('Error loading realizations:', error);
        setVideos(isVideoBooth ? DEFAULT_VIDEOBOOTH_SHORTS : DEFAULT_LIVREDOOR_VIDEOS);
      } finally {
        setLoading(false);
      }
    };
    
    loadRealizations();
  }, [serviceType, isVideoBooth]);

  const title = isVideoBooth 
    ? 'Nos réalisations VideoBooth 360' 
    : 'Nos montages vidéos en 4K';

  return (
    <div className="min-h-screen bg-black" data-testid="realisations-page">
      {/* Header Section */}
      <div className="bg-gradient-to-b from-gray-900 to-black py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="realisations-title">
            {isVideoBooth ? (
              <>Nos réalisations <span className="gold-shimmer-text">VideoBooth 360°</span></>
            ) : (
              <>Nos montages vidéos en <span className="gold-shimmer-text">4K</span></>
            )}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {isVideoBooth 
              ? 'Découvrez nos réalisations VideoBooth 360° lors de différents événements'
              : 'Découvrez nos montages vidéo personnalisés pour vos événements'}
          </p>
        </div>
      </div>

      {/* Videos Grid */}
      <section className="py-12 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
            </div>
          ) : isVideoBooth ? (
            // Grid for YouTube Shorts (vertical format)
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {videos.map((video) => (
                <YouTubeShort key={video.id} videoId={video.id} title={video.title} />
              ))}
            </div>
          ) : (
            // Grid for normal YouTube videos (horizontal format)
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {videos.map((video) => (
                <div key={video.id}>
                  <YouTubeVideo videoId={video.id} title={video.title} />
                  <p className="mt-3 text-center text-white/80 font-medium">{video.title}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section - Black and Gold theme */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {isLivreDor ? (
              <>L'écran d'accueil du Livre d'Or Vidéo est <span className="text-[#e6d28e]">personnalisable</span> en fonction de votre événement</>
            ) : (
              <>Nous réalisons <span className="text-[#e6d28e]">vos modèles sur mesure</span></>
            )}
          </h2>
          <p className="text-white/80 text-lg mb-8">
            {isLivreDor 
              ? "Donnez vos instructions à nos graphistes pour sa création."
              : "Qu'ils soient verticaux ou horizontaux, communiquez vos instructions à nos graphistes."
            }
          </p>
          <a 
            href="/contact" 
            className="inline-block bg-[#e6d28e] text-black font-semibold px-8 py-3 rounded-full hover:bg-[#d4c67a] transition-colors shadow-lg"
            data-testid="contact-cta"
          >
            <FiPhone className="inline-block mr-2" />
            Nous contacter
          </a>
        </div>
      </section>
    </div>
  );
}
